/** Jobs and photos (S50 to S53), the photo library and uploads. docs/50 §4.5, docs/53 §7.8. */
import { z } from 'zod';
import { AppPath, Cursor, HttpsUrl, IsoDateTime, Limit, text } from '../common.ts';
import { DB_ENUMS, JobStatus, MediaSource, OutputKind, OutputState, ScreenState } from '../enums.ts';
import { JobId, LocationId, MediaAssetId, OutputId, ServiceId, TownId, UploadId, UserId } from '../ids.ts';
import { PhotoKind, PhotoNeeds, PhotoProblem } from '../media.ts';

/**
 * docs/53 §7.8 limits. A photo is at most 20 MB: that's what the Images binding takes as input (docs/20 §8.1), and
 * the app has already resized a phone photo to 2,400 px before it uploads (a raw 48 megapixel HEIC is about 10 MB).
 */
export const UPLOAD_LIMITS = {
  photoBytes: 20 * 1024 * 1024,
  videoBytes: 200 * 1024 * 1024,
  voiceBytes: 25 * 1024 * 1024,
  documentBytes: 25 * 1024 * 1024,
  filesPerJob: 50,
  filesPerTenantPerDay: 500,
  photoLongEdgePx: 2400,
  multipartPartBytes: 10 * 1024 * 1024,
} as const;

export const MediaView = z.object({
  id: MediaAssetId,
  /** App-origin path served after a permission check; never a public bucket URL (docs/53 §5.2). */
  thumbUrl: AppPath,
  fullUrl: AppPath,
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  source: MediaSource,
  screenState: ScreenState,
  /** Plain words for a flagged photo ("There's a house number in this one"). */
  screenNote: z.string().max(160).nullable(),
  excluded: z.boolean(),
  usedIn: z.array(z.object({ kind: OutputKind, url: HttpsUrl.nullable() })),
  createdAt: IsoDateTime,
  // Added with the photo pipeline (k11). Optional so an older response still parses.
  /** The client's own name for the photo ("Hendersons' water heater"). Never shown on the site. */
  title: z.string().max(80).nullable().optional(),
  /** What it shows, as the client or we said. Null until someone says. */
  kind: PhotoKind.nullable().optional(),
  /** A favourite is one the client wants used first. */
  favourite: z.boolean().optional(),
  /** True while the pipeline is still checking it: there's nothing to show yet. */
  processing: z.boolean().optional(),
  /** Why it can't be used, as machine reasons; `screenNote` carries the sentence. Empty when it can. */
  problems: z.array(PhotoProblem).max(6).optional(),
  /** The service the client says it shows. */
  serviceId: ServiceId.nullable().optional(),
  /** Where it sits on their site: page slots by name, services by name. Empty when it isn't placed. */
  placedIn: z.array(z.string().max(80)).max(30).optional(),
  /** Whether the caller may retitle, favourite or remove it. */
  canEdit: z.boolean().optional(),
});

export const JobOutputView = z.object({
  id: OutputId,
  kind: OutputKind,
  state: OutputState,
  liveUrl: HttpsUrl.nullable(),
  publishedAt: IsoDateTime.nullable(),
});

export const JobSummary = z.object({
  id: JobId,
  status: JobStatus,
  note: z.string().max(2000).nullable(),
  townName: z.string().max(80).nullable(),
  serviceName: z.string().max(80).nullable(),
  coverThumbUrl: AppPath.nullable(),
  photoCount: z.number().int().min(0),
  createdBy: UserId.nullable(),
  createdAt: IsoDateTime,
  publishedAt: IsoDateTime.nullable(),
  locationId: LocationId.nullable(),
});

export const JobDetail = JobSummary.extend({
  transcript: z.string().max(10_000).nullable(),
  townId: TownId.nullable(),
  serviceId: ServiceId.nullable(),
  media: z.array(MediaView),
  outputs: z.array(JobOutputView),
});

export const JobListQuery = z
  .object({ location: LocationId.optional(), mine: z.enum(['0', '1']).optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();
export const JobList = z.object({ items: z.array(JobSummary), nextCursor: Cursor.nullable() });

export const JobCreateBody = z
  .object({
    note: text(2000).optional(),
    voiceUploadId: UploadId.optional(),
    uploadIds: z.array(UploadId).max(UPLOAD_LIMITS.filesPerJob),
    townId: TownId.optional(),
    serviceId: ServiceId.optional(),
    locationId: LocationId.optional(),
    /** Offline queue key: a retried submit creates one job (docs/55 E13). */
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  })
  .strict()
  .refine((b) => b.uploadIds.length > 0 || b.note !== undefined || b.voiceUploadId !== undefined, {
    message: 'add a photo or say what you did',
    path: ['uploadIds'],
  });

export const JobCreated = z.object({ job: JobDetail, readyBy: z.iso.date() });

/**
 * The Town and Service pickers on S51 (docs/52 §3.6). Crew can add jobs but can't read facts, so the pickers need
 * their own read. `center` lets the app suggest the town from a photo's location on the device; the location
 * itself is never uploaded (the photo is re-encoded without it).
 */
export const JobOptionsQuery = z.object({ location: LocationId.optional() }).strict();
export const JobOptions = z.object({
  towns: z.array(
    z.object({
      id: TownId,
      name: z.string().max(80),
      center: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable(),
    }),
  ),
  services: z.array(z.object({ id: ServiceId, name: z.string().max(80) })),
});

export const JobUpdateBody = z
  .object({ note: text(2000).optional(), townId: TownId.nullable().optional(), serviceId: ServiceId.nullable().optional(), addUploadIds: z.array(UploadId).max(50).optional() })
  .strict();

/** "Don't use this photo" (docs/52 S52). */
export const JobMediaBody = z.object({ excluded: z.boolean() }).strict();

export const MediaListQuery = z
  .object({ location: LocationId.optional(), filter: z.enum(['all', 'used', 'unused', 'flagged', 'favourites']).optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();
/** `size=thumb` answers the small variant once the media pipeline has made it, else the original. Added by WP-API. */
export const MediaFileQuery = z.object({ size: z.enum(['thumb', 'full']).optional() }).strict();

export const MediaList = z.object({
  items: z.array(MediaView),
  nextCursor: Cursor.nullable(),
  /** Before go-live: what the site still needs from them (c057). Absent once live, and for crew. */
  needs: PhotoNeeds.nullable().optional(),
});

/**
 * The client's own edits to a photo in their library: a name, a favourite, what it shows, the service it shows and
 * "don't use this one". Saying what a photo shows after we approved it sends it back for our look.
 */
export const MediaUpdateBody = z
  .object({
    title: z.union([text(80), z.literal('')]).nullable().optional(),
    favourite: z.boolean().optional(),
    kind: PhotoKind.optional(),
    serviceId: ServiceId.nullable().optional(),
    excluded: z.boolean().optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, { message: 'nothing to change' });

/** What removing a photo did. A photo on the live site leaves it at the next publish, which removing it starts. */
export const MediaRemoved = z.object({ ok: z.literal(true), wasOnSite: z.boolean() });

// ---------------------------------------------------------------------------------------------- uploads

export const UploadKind = z.enum(DB_ENUMS['uploads.kind']);
export const UploadPurpose = z.enum(DB_ENUMS['uploads.purpose']);

const ALLOWED_TYPES = {
  photo: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
  video: ['video/mp4', 'video/quicktime', 'video/webm'],
  voice: ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/aac'],
  document: ['application/pdf', 'text/csv', 'image/jpeg', 'image/png'],
} as const;
export const UPLOAD_CONTENT_TYPES = ALLOWED_TYPES;

export const UploadCreateBody = z
  .object({
    kind: UploadKind,
    purpose: UploadPurpose,
    contentType: z.string().max(100),
    bytes: z.number().int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
    fileName: text(200).optional(),
  })
  .strict()
  .superRefine((b, ctx) => {
    const types: readonly string[] = ALLOWED_TYPES[b.kind];
    if (!types.includes(b.contentType)) ctx.addIssue({ code: 'custom', message: 'unsupported file type', path: ['contentType'] });
    const max =
      b.kind === 'photo' ? UPLOAD_LIMITS.photoBytes : b.kind === 'video' ? UPLOAD_LIMITS.videoBytes : b.kind === 'voice' ? UPLOAD_LIMITS.voiceBytes : UPLOAD_LIMITS.documentBytes;
    if (b.bytes > max) ctx.addIssue({ code: 'custom', message: 'file too large', path: ['bytes'] });
  });

export const UploadCreated = z.object({
  uploadId: UploadId,
  /** Relative path for the PUT (same origin, same session). */
  putPath: AppPath,
  maxBytes: z.number().int().positive(),
  multipart: z.object({ partBytes: z.number().int().positive(), parts: z.number().int().positive() }).nullable(),
  expiresAt: IsoDateTime,
});

/** Query for PUT /uploads/:id. `part` is 1-based and present only for multipart uploads. */
export const UploadPutQuery = z.object({ part: z.coerce.number().int().min(1).max(10_000).optional() }).strict();
export const UploadPutResponse = z.object({ receivedBytes: z.number().int().min(0), part: z.number().int().min(1).nullable() });

/**
 * `kind` and `serviceId` are what the client says a photo shows, given as they pick it (the get live step asks,
 * the library asks); `title` is their own name for it. All three are optional and only read for photos.
 */
export const UploadCompleteBody = z
  .object({ parts: z.number().int().min(1).max(10_000).optional(), kind: PhotoKind.optional(), serviceId: ServiceId.optional(), title: text(80).optional() })
  .strict();
export const UploadCompleted = z.object({ uploadId: UploadId, assetId: MediaAssetId.nullable(), state: z.enum(['processing', 'ready', 'rejected']) });
